import {green} from "@ant-design/colors";
import {CheckCircleFilled, InfoCircleTwoTone} from "@ant-design/icons";
import {Alert, Button, Checkbox, Divider, message, Modal, Result, Space, Tooltip} from "antd";
import {FetchResult} from "@apollo/client";
import React, {memo, FC, useMemo, useState, useEffect, useRef} from "react";
import {useHistory} from "react-router-dom";
import {handleError, KnownError} from "../../errors/handleErrors";
import {
    ArrayDataOptions,
    BookingType,
    CopyAttendeeToCourseMutation,
    GetBookingsByAttendeeIdWithOptionsDocument,
    GetBookingsByCourseDocument,
    GetBookingsByCourseMinimalDocument,
    GetCourseDetailsByIdDocument,
    GetCoursesWithPaginationQuery,
    PaymentProvider,
    TablesEnum,
    TransferAttendeeToCourseMutation,
    useCopyAttendeeToCourseMutation,
    useGetBookingByIdQuery,
    useGetBookingsByCourseMinimalQuery,
    useGetCoursesWithPaginationQuery,
    useRefundMolliePaymentFromAdminMutation,
    useTransferAttendeeToCourseMutation,
    useVoidMolliePaymentLinkForBookingMutation,
} from "../../generated/graphql";
import {URL_COURSES_DETAILS_ID, URL_INSTRUCTOR_BOOKING_EDIT_ID} from "../../models/url";
import {getBookingDetailsPath} from "../../helpers/bookingDetailsRouting";
import CourseList from "../courses/CourseEdit/CourseList";
import {TableType} from "../courses/CourseEdit/CourseListColumns";
import css from "./TransferAttendeeToAnotherCourse.less";
import {useLocalStorageOptions} from "../../helpers/columnsLocalStorageHandler";
import {marginS} from "../../styles/layout";
import useHighestRole from "../../helpers/useHighestRole";
import Roles from "../../../shared/models/roles";
import {useIsStaffOnlyAccess} from "../../helpers/useIsStaffOnlyAccess";
import RefundPanel, {RefundPanelRow, RefundChoice} from "../../views/RefundPanel/RefundPanel";
import i18n from "../../services/i18n";
import useTransferWizardMode from "../../hooks/useTransferWizardMode";
import TransferWizard from "./TransferWizard/TransferWizard";
import ActiveDunningAlert from "../invoice/SettlementDetails/BookingDunnings/ActiveDunningAlert";

type TransferAttendeeToAnotherCourseProps = {
    transformAction: "transfer" | "copy";
    bookingId: string;
    currentCourseId: string;
    modalOpen: boolean;
    setModalOpen: Function;
    onCancel?: Function;
    attendeeFullName?: string;
    attendeeId?: string;
    isSettled?: boolean;
};

const TransferAttendeeToAnotherCourse: FC<TransferAttendeeToAnotherCourseProps> = ({transformAction, bookingId, currentCourseId, modalOpen, setModalOpen, attendeeFullName, attendeeId, isSettled}) => {
    const history = useHistory();
    const highestRole = useHighestRole();
    // Umbuchungs-Engine (#660/#662): nur der echte Transfer laeuft ueber die Engine — im
    // Wizard-Modus rendert diese Komponente den `TransferWizard` statt des Legacy-Dialogs.
    // Kopieren ist keine Umbuchung (die alte Buchung bleibt) und der Platzreservierungs-
    // Zweig bleibt bewusst Legacy (die Engine kennt kein Geldmodell dafuer); der Wizard
    // bekommt den Knopf als Render-Prop und meldet seine Kursauswahl hierher zurueck.
    const {wizardMode, loading: transferModeLoading, unavailable: transferModeUnavailable} = useTransferWizardMode();
    const transferModePending = transformAction === "transfer" && (transferModeLoading || transferModeUnavailable === true);
    const engineTransferEnabled = wizardMode && transformAction === "transfer";
    // Mitarbeitenden-Zugaenge (Kursleitung, Assistenz, Staff, Bereichsleitung, BetaTester)
    // duerfen laut E1 im Wizard-Modus nur unbezahlte, nicht abgerechnete Buchungen umbuchen;
    // der Legacy-Platzreservierungs-Zweig wuerde das umgehen und bleibt dort fuer sie zu.
    // Ohne Wizard-Modus (und beim Kopieren) gilt das alte Verhalten: alles offen.
    // #809: Klassifikation ueber den VOLLEN Rollensatz und exakt dieselbe Regel wie der
    // Server-Guard `isOnlyStaffAccess` — vorher wurde nur `highestRole` gegen Admin geprueft,
    // was Assistant-/BetaTester-only-Logins falsch einsortierte.
    const isStaffOnlyAccess = useIsStaffOnlyAccess();
    const placeReservationBlocked = isStaffOnlyAccess && engineTransferEnabled;
    const [selectedCourse, setSelectedCourse] = useState<TableType | undefined>(undefined);
    const [deleteNotes, setDeleteNotes] = useState<boolean>(false);
    // const [options, setOptions] = useState<ArrayDataOptions>({});
    const localStorageName = "transferAttendeeToCourse";
    const [totalResults, setTotalResults] = useState<number>(0);
    const [options, setOptions] = useLocalStorageOptions({
        localStorageName,
        // defaultOptions: standadOptions,
        defaultOptions: {},
        totalResults,
    });

    // COURSELIST DATA -------------------------------------------------------------------------------------
    // const emptyOptions = Object.keys(options ?? {}).length === 0;
    const {
        data: coursesData,
        loading: loadingCourses,
        refetch: refetchCourses,
    } = useGetCoursesWithPaginationQuery({
        variables: {options: options as ArrayDataOptions},
    });

    useEffect(() => {
        totalResults !== coursesData?.coursesWithPagination?.total && setTotalResults(coursesData?.coursesWithPagination?.total ?? 0);
    }, [coursesData, totalResults]);

    const courses: GetCoursesWithPaginationQuery["coursesWithPagination"]["items"] = coursesData?.coursesWithPagination?.items ?? [];

    const coursesWithoutCurrentCourse = courses.filter((course) => course.id !== currentCourseId);

    const tableData = {
        dataSource: coursesWithoutCurrentCourse as Array<TableType>,
        loading: loadingCourses,
        total: coursesData?.coursesWithPagination?.total ?? 0,
    };

    // CHECKING FOR PLACE RESERVATIONa ----------------------------------------------------------------------
    const {data: bookingsByCourseData} = useGetBookingsByCourseMinimalQuery({
        skip: !selectedCourse?.id,
        variables: {id: selectedCourse?.id ?? ""},
    });

    const bookingsIncludingCanceled = bookingsByCourseData?.getBookingsByCourse["items"];
    const allBookings = bookingsIncludingCanceled?.filter((booking) => {
        return booking.canceled === null;
    });

    const placervationsEntries =
        allBookings?.filter((booking) => {
            return booking.bookingNumberPrefix === "P";
        }) ?? [];

    // ACTION --------------------------------------------------------------------------------------

    const knownErrors: Array<KnownError> = [
        {
            key: "Booking has no attendee",
            title: "Buchung ohne Teilnehmer",
            msg: "Diese Buchung hat keinen Teilnehmer.",
        },
        {
            key: "Booking is of type PlaceReservation",
            title: "Buchung ist vom Typ Platzreservierung",
            msg: "Platzreservierurngen können nicht verschoben werden.",
        },
        {
            key: "Booking is settled",
            title: "Buchung ist abgerechnet",
            msg: "Abgerechnete Buchungen können nicht verschoben werden.",
        },
        {
            key: "Target course not found",
            title: "Zielkurs nicht gefunden",
            msg: "Der Zielkurs konnte nicht gefunden werden.",
        },
        {
            key: "Already enrolled",
            title: "Doppelbuchung",
            msg: "Dieser Teilnehmer ist bereits in diesem Kurs gebucht, oder steht auf dessen Warteliste.",
        },
    ];

    // Refetch-Scope einer Umbuchung: Quell- und Zielkurs, saemtliche Buchungslisten und die
    // Buchungen des Teilnehmers (derselbe Scope wie im `TransferWizard`).
    const transferRefetchQueries = [
        "GetBookingsByCourseMinimal",
        {
            query: GetBookingsByCourseMinimalDocument,
            variables: {id: selectedCourse?.id},
        },
        {
            query: GetBookingsByCourseMinimalDocument,
            variables: {id: currentCourseId},
        },
        // "GetCourseLessonsByCourseId",
        "GetBookingsByCourse",
        {
            query: GetBookingsByCourseDocument,
            variables: {id: selectedCourse?.id},
        },
        {
            query: GetBookingsByCourseDocument,
            variables: {id: currentCourseId},
        },
        "GetCourseDetailsById",
        {
            query: GetCourseDetailsByIdDocument,
            variables: {id: selectedCourse?.id},
        },
        {
            query: GetCourseDetailsByIdDocument,
            variables: {id: currentCourseId},
        },
        "GetCoursesWithPagination",
        "getFreePlacesByCourseId",
        "GetSettledBookings",
        "GetUnsettledBookings",
        "GetBookingsOverviewOptimized",
        {
            query: GetBookingsByAttendeeIdWithOptionsDocument,
            variables: {id: attendeeId ?? "", canceledBookings: false},
        },
    ];

    const [transferAttendee, {loading: transferLoading}] = useTransferAttendeeToCourseMutation({
        refetchQueries: transferRefetchQueries,
    });

    const [copyBooking, {loading: copyLoading}] = useCopyAttendeeToCourseMutation({
        refetchQueries: [
            "GetBookingsByCourseMinimal",
            {
                query: GetBookingsByCourseMinimalDocument,
                variables: {id: selectedCourse?.id},
            },
            {
                query: GetBookingsByCourseMinimalDocument,
                variables: {id: currentCourseId},
            },
            // "GetCourseLessonsByCourseId",
            "GetBookingsByCourse",
            {
                query: GetBookingsByCourseDocument,
                variables: {id: selectedCourse?.id},
            },
            {
                query: GetBookingsByCourseDocument,
                variables: {id: currentCourseId},
            },
            "GetCourseDetailsById",
            {
                query: GetCourseDetailsByIdDocument,
                variables: {id: selectedCourse?.id},
            },
            {
                query: GetCourseDetailsByIdDocument,
                variables: {id: currentCourseId},
            },
            "GetCoursesWithPagination",
            "getFreePlacesByCourseId",
            "GetSettledBookings",
            "GetUnsettledBookings",
            "GetBookingsOverviewOptimized",
            "GetBookingsByAttendeeIdWithOptions",
            {
                query: GetBookingsByAttendeeIdWithOptionsDocument,
                variables: {id: attendeeId ?? "", canceledBookings: false},
            },
        ],
    });

    // Transfer-FK / Link-Void: Entwertet einen noch gültigen Mollie-Zahlungslink,
    // bevor die Umbuchung erneut versucht wird. Gleicher Refetch-Scope wie die
    // Transfer-Mutation, damit die Buchungslisten nach dem Entwerten konsistent
    // sind.
    const [voidLink] = useVoidMolliePaymentLinkForBookingMutation({
        refetchQueries: [
            "GetBookingsByCourseMinimal",
            {
                query: GetBookingsByCourseMinimalDocument,
                variables: {id: selectedCourse?.id},
            },
            {
                query: GetBookingsByCourseMinimalDocument,
                variables: {id: currentCourseId},
            },
            "GetBookingsByCourse",
            {
                query: GetBookingsByCourseDocument,
                variables: {id: selectedCourse?.id},
            },
            {
                query: GetBookingsByCourseDocument,
                variables: {id: currentCourseId},
            },
            "GetCourseDetailsById",
            {
                query: GetCourseDetailsByIdDocument,
                variables: {id: selectedCourse?.id},
            },
            {
                query: GetCourseDetailsByIdDocument,
                variables: {id: currentCourseId},
            },
        ],
    });

    // Verhindere mehrfache gleichzeitige Operationen.
    const isOperationInProgress = transferLoading || copyLoading;

    // Phase 14 / Plan 14-04: Refund-Panel-State + Mollie-SP-Extraktion
    // Nur relevant wenn umgebucht wird UND die aktuelle Buchung bereits abgerechnet ist
    // (isSettled === true), weil nur dann ein Storno-Flow mit Refund-Bedarf entsteht.
    const [pendingRefunds, setPendingRefunds] = useState<{
        refunds: Array<{scheduledPaymentId: string; amount: number; kind: RefundChoice}>;
        reason: string;
    } | null>(null);
    const executedRefundKeysRef = useRef<Set<string>>(new Set());
    const [refundMolliePayment] = useRefundMolliePaymentFromAdminMutation();

    const {data: bookingByIdData} = useGetBookingByIdQuery({
        variables: {id: bookingId},
        skip: !modalOpen || !bookingId || transformAction !== "transfer" || !isSettled,
        fetchPolicy: "cache-and-network",
    });

    // Bug-Fix Mollie-Refund-Cap: Cap = (molliePaidAmount ?? paymentAmount) - refundedAmount.
    // molliePaidAmount kann null/undefined sein → Fallback auf paymentAmount (= altes
    // Verhalten `refundedAmount < paymentAmount`). Voll-erstattete Raten (Cap = 0) werden
    // gar nicht erst ans Panel übergeben, damit keine "Ghost-Row" entsteht.
    const mollieRefundableSps: Array<RefundPanelRow> = useMemo(() => {
        const sps = bookingByIdData?.bookingById?.scheduledPayments ?? [];

        return sps
            .filter(
                (sp) =>
                    sp.paymentProvider === PaymentProvider.Mollie
                    && sp.paymentDone !== null
                    && sp.paymentDone !== undefined
                    && (sp.molliePaidAmount ?? sp.paymentAmount) - Number(sp.refundedAmount || 0) > 0,
            )
            .map((sp) => ({
                scheduledPaymentId: sp.id,
                paymentAmount: Number(sp.paymentAmount),
                refundedAmount: Number(sp.refundedAmount || 0),
                paymentDue: sp.paymentDue,
                paymentDone: sp.paymentDone ?? null,
                title: sp.title ?? null,
                paymentProvider: sp.paymentProvider,
                mollieMethod: sp.mollieMethod,
                molliePaidAmount: sp.molliePaidAmount,
            }));
    }, [bookingByIdData]);

    // Modal kann fuer eine andere Buchung wiederverwendet werden: Refund-Dedupe darf nie
    // buchungsuebergreifend erhalten bleiben.
    useEffect(() => {
        executedRefundKeysRef.current = new Set();
    }, [bookingId]);

    const buildRefundExecutionKey = (refund: {scheduledPaymentId: string; amount: number; kind: RefundChoice}, reason: string): string => {
        return [refund.scheduledPaymentId, refund.kind, refund.amount, reason].join("|");
    };

    const handleRefundSelectionChange = (selection: {refunds: Array<{scheduledPaymentId: string; amount: number; kind: RefundChoice}>; reason: string}) => {
        // Neue Auswahl = neuer Refund-Wille des Users; ein spaeterer Versuch darf diese Auswahl frisch ausfuehren.
        executedRefundKeysRef.current = new Set();
        setPendingRefunds(selection);
    };

    type ResultModalProps = {
        attendeeFullName: string;
        course: {id: string; prefixedCourseNumber?: string | null; allowLessonBooking?: boolean | null};
        bookingType?: BookingType;
        newBookingId?: string;
        transformAction?: "transfer" | "copy";
    };

    const ResultModal: FC<ResultModalProps> = ({attendeeFullName, course, bookingType, newBookingId, transformAction}) => {
        const linkToCourse = course.id
            ? {
                  pathname: URL_COURSES_DETAILS_ID.replace(":id", course.id),
                  state: {returnTo: window.location.pathname},
              }
            : undefined;
        const linkToNewBooking = newBookingId
            ? isStaffOnlyAccess
                ? URL_INSTRUCTOR_BOOKING_EDIT_ID.replace(":id", newBookingId)
                : getBookingDetailsPath(newBookingId, false, Boolean(course.allowLessonBooking), highestRole, window.location.pathname)
            : undefined;

        const ResultTextCopy = () => {
            if (bookingType === BookingType.Waitlist) {
                return (
                    <div className={css.resultTitle}>
                        <div className={css.resultTitleHighlighted}>{`${attendeeFullName}`}</div>
                        <div>{`wurde auf die Warteliste des Kurses`}</div>
                        <div className={css.resultTitleHighlighted}>{`${course.prefixedCourseNumber}`}</div>
                        <div>{`kopiert.`}</div>
                    </div>
                );
            }

            return (
                <div className={css.resultTitle}>
                    <div className={css.resultTitleHighlighted}>{`${attendeeFullName}`}</div>
                    <div>{`wurde erfolgreich in den Kurs`}</div>
                    <div className={css.resultTitleHighlighted}>{`${course.prefixedCourseNumber}`}</div>
                    <div>{`kopiert.`}</div>
                </div>
            );
        };

        const ResultTextTransfer = () => {
            if (bookingType === BookingType.Waitlist) {
                return (
                    <div className={css.resultTitle}>
                        <div className={css.resultTitleHighlighted}>{`${attendeeFullName}`}</div>
                        <div>{`wurde auf die Warteliste des Kurses`}</div>
                        <div className={css.resultTitleHighlighted}>{`${course.prefixedCourseNumber}`}</div>
                        <div>{`verschoben.`}</div>
                    </div>
                );
            }

            return (
                <div className={css.resultTitle}>
                    <div className={css.resultTitleHighlighted}>{`${attendeeFullName}`}</div>
                    <div>{`wurde erfolgreich in den Kurs`}</div>
                    <div className={css.resultTitleHighlighted}>{`${course.prefixedCourseNumber}`}</div>
                    <div>{`verschoben.`}</div>
                </div>
            );
        };

        const handleGoToLink = async (link: string | {pathname: string; state: {returnTo: string}}) => {
            setSelectedCourse(undefined);
            setModalOpen(false);
            history.push(link);
            Modal.destroyAll();
        };

        return (
            <Result
                icon={<CheckCircleFilled style={{fontSize: 50, color: green[3]}} />}
                status="success"
                style={{width: "100%", padding: 0}}
                // style={{padding: 30}}
                title={transformAction === "copy" ? <ResultTextCopy /> : <ResultTextTransfer />}
                subTitle={
                    <div className={css.resultSubTitle}>
                        {transformAction === "copy" ? (
                            `Der Teilnehmer ist jetzt in beiden Kursen eingetragen.`
                        ) : (
                            <span>
                                <p>{`Der Platz im bisherigen Kurs ist damit verfügbar und kann neu vergeben werden.`}</p>
                                {isSettled && <p style={{fontWeight: 500}}>{`Die bisherige Buchung wurde storniert und es wurde eine Stornorechnung generiert.`}</p>}
                            </span>
                        )}
                    </div>
                }
                extra={
                    <Space style={{width: "100%", justifyContent: "space-around"}}>
                        {linkToCourse && (
                            <Button type="link" style={{minWidth: "40%"}} onClick={async () => handleGoToLink(linkToCourse)}>
                                {"Zum Kurs"}
                            </Button>
                        )}
                        {linkToNewBooking && (
                            <Button type="link" style={{minWidth: "40%"}} onClick={async () => handleGoToLink(linkToNewBooking)}>
                                {"Zur neuen Buchung"}
                            </Button>
                        )}
                    </Space>
                }
            />
        );
    };

    type ResultType = FetchResult<CopyAttendeeToCourseMutation> | FetchResult<TransferAttendeeToCourseMutation>;

    const isCopyAttendeeToCourse = (result: ResultType["data"]): result is CopyAttendeeToCourseMutation => {
        return result !== null && result !== undefined && "copyAttendeeToCourse" in result;
    };

    // Erfolgs-Abschluss des Legacy-Pfads: Toast + Ergebnis-Dialog.
    const showTransferSuccess = ({
        attendeeFullName,
        messagePrefixedCourseNumber,
        course,
        bookingType,
        newBookingId,
        transformAction,
    }: {
        attendeeFullName: string;
        messagePrefixedCourseNumber?: string | null;
        course: {id: string; prefixedCourseNumber?: string | null; allowLessonBooking?: boolean | null};
        bookingType?: BookingType;
        newBookingId?: string;
        transformAction: "transfer" | "copy";
    }) => {
        if (bookingType === BookingType.Booking) {
            message.success(`${attendeeFullName} wurde erfolgreich in den Kurs ${messagePrefixedCourseNumber} gebucht.`);
        } else {
            message.success(`${attendeeFullName} wurde erfolgreich auf die Warteliste des Kurses ${messagePrefixedCourseNumber} gesetzt.`);
        }

        Modal.confirm({
            icon: null,
            content: (
                <ResultModal
                    attendeeFullName={attendeeFullName}
                    course={course}
                    bookingType={bookingType}
                    newBookingId={newBookingId}
                    transformAction={transformAction}
                />
            ),
            onOk: () => {
                setSelectedCourse(undefined);
                setModalOpen(false);
            },
            okButtonProps: {
                block: true,
                style: {margin: 0, paddingLeft: 0},
            },
            cancelButtonProps: {style: {display: "none"}},
            maskClosable: true,
        });
    };

    const handleSuccess = (res: ResultType, selectedCourse: TableType, transformAction: "transfer" | "copy") => {
        // const result = res.data;
        const resultData = res.data;

        if (!resultData) return;

        const result = isCopyAttendeeToCourse(resultData) ? resultData.copyAttendeeToCourse : resultData.transferAttendeeToCourse;

        const {prefixedCourseNumber} = selectedCourse;
        const attendeeFullName = `${result?.attendee?.firstname} ${result?.attendee?.lastname}`;

        showTransferSuccess({
            attendeeFullName,
            messagePrefixedCourseNumber: prefixedCourseNumber,
            course: result.courseLessons[0].course,
            bookingType: result.bookingType as BookingType,
            newBookingId: result.id,
            transformAction,
        });
    };

    // Phase 14 / D-07 + D-10: Führt ausgewählte Refunds (inkl. Transfer-Kind)
    // VOR dem Transfer-/Copy-Mutation-Call aus. Bei Fehler bricht der Flow ab —
    // weder Refund-Rest noch Transfer werden ausgeführt. Audit-Events kommen aus
    // der Server-Mutation (Plan 14-02). Der Transfer-Kind löst serverseitig
    // aktuell einen normalen Refund aus; "mollie_refund_transfer_initiated" als
    // eigenes Audit-Event bleibt als Gap für Plan 14-06 dokumentiert.
    const executePendingRefunds = async (): Promise<boolean> => {
        if (!pendingRefunds || pendingRefunds.refunds.length === 0) return true;
        for (const refund of pendingRefunds.refunds) {
            if (refund.kind === "none") continue;
            // "Transfer auf neue Buchung": KEIN Mollie-Refund. Das bereits gezahlte
            // Guthaben wird vom Transfer-Service über `paidAmountToTransfer` auf die
            // neue Buchung verrechnet (Preis-Reduktion). Ein zusätzlicher echter
            // Mollie-Refund würde den Betrag an den Kunden zurückzahlen UND ihn auf
            // der neuen Buchung gutschreiben — also Doppel-Gutschrift / Geldverlust.
            // (Voll-/Teil-Refund hingegen zahlen bewusst an den Kunden zurück.)
            if (refund.kind === "transfer") {
                message.info(i18n.views.RefundPanel.transferInfoToast(), 4);
                continue;
            }
            const refundExecutionKey = buildRefundExecutionKey(refund, pendingRefunds.reason || "");

            if (executedRefundKeysRef.current.has(refundExecutionKey)) {
                continue;
            }
            await refundMolliePayment({
                variables: {
                    scheduledPaymentId: refund.scheduledPaymentId,
                    amount: refund.amount,
                    reason: pendingRefunds.reason || undefined,
                },
            });
            executedRefundKeysRef.current.add(refundExecutionKey);
        }

        return true;
    };

    // Transfer-FK / Link-Void: Extrahiert den GraphQL-Error-Code aus einem
    // Apollo-Error. Der Server legt den handlungsweisenden Code direkt in
    // `extensions.code` ab (ApolloError-Konstruktor), siehe
    // mollie.errorCodes.ts / classifyMollieDispatchError. Wir matchen auf den
    // Code, NICHT auf die Message (die enthält den Code-String nicht).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const extractGraphQlErrorCode = (error: any): string | undefined => {
        return error?.graphQLErrors?.[0]?.extensions ? error.graphQLErrors[0].extensions.code : undefined;
    };

    // Transfer-FK / Link-Void: Gemeinsames Code-Routing für beide Transfer-Pfade.
    // Bei aktivem Mollie-Zahlungslink (Code `MOLLIE_ACTIVE_PAYMENT_LINK`) wird ein
    // Bestätigungs-Dialog geöffnet; nach Bestätigung wird der Link entwertet und
    // die identische Umbuchung erneut ausgeführt (retry-Callback). Bei allen
    // anderen Fehlern greift das bestehende generische handleError-Verhalten.
    // Liefert `true`, wenn der Fehler behandelt wurde (kein weiteres handleError).
    const handleTransferError = (errorContext: string, error: unknown, retry: () => Promise<void>): boolean => {
        const code = extractGraphQlErrorCode(error);

        if (code === "MOLLIE_ACTIVE_PAYMENT_LINK") {
            Modal.confirm({
                // Breiter, damit der lange OK-Text ("Zahlungslink jetzt entwerten und
                // umbuchen") + "Abbrechen" in einer Zeile nebeneinander passen.
                width: 600,
                okButtonProps: {style: {whiteSpace: "nowrap"}},
                icon: null,
                title: i18n.containers.bookings.TransferMolliePaymentLink.single.modalTitle(),
                content: i18n.containers.bookings.TransferMolliePaymentLink.single.modalContent(),
                okText: i18n.containers.bookings.TransferMolliePaymentLink.single.okText(),
                onCancel: () => {},
                onOk: async () => {
                    try {
                        message.loading(i18n.containers.bookings.TransferMolliePaymentLink.single.voidingMessage(), 0);
                        await voidLink({variables: {bookingId}});
                        message.destroy();
                        await retry();
                    } catch (retryError) {
                        message.destroy();
                        message.error(i18n.containers.bookings.TransferMolliePaymentLink.single.errorMessage(), 5);
                        handleError(errorContext, retryError, "modal", knownErrors);
                    }
                },
            });

            return true;
        }

        return false;
    };

    const transferOrCopyAttendeeToAnotherCourse = async () => {
        const selectedCourseId = selectedCourse?.id;

        if (!selectedCourseId || !selectedCourse || transferModePending) {
            return;
        }

        const runTransfer = async () => {
            // Refunds zuerst ausführen (nur beim echten Transfer relevant)
            if (transformAction === "transfer" && isSettled) {
                await executePendingRefunds();
            }
            if (transformAction === "copy") {
                await copyBooking({
                    variables: {
                        bookingId,
                        targetCourseId: selectedCourseId,
                        deleteNotes,
                    },
                }).then((res) => {
                    handleSuccess(res, selectedCourse, transformAction);
                });
            } else {
                await transferAttendee({
                    variables: {
                        bookingId,
                        targetCourseId: selectedCourseId,
                        deleteNotes,
                    },
                }).then((res) => {
                    handleSuccess(res, selectedCourse, transformAction);
                });
            }
        };

        try {
            await runTransfer();
        } catch (error) {
            // Transfer-FK / Link-Void: aktiver Zahlungslink → Void→Retry, sonst generisch.
            if (handleTransferError("transferAttendeeToCourse", error, runTransfer)) {
                return;
            }
            message.error(`Refund/Umbuchung fehlgeschlagen: ${error instanceof Error ? error.message : String(error)}`, 5);
            handleError("transferAttendeeToCourse", error, "modal", knownErrors);
        }
    };

    const transferOrCopyAttendeeToAnotherCoursePlacereservation = async (usePlaceReservation?: boolean) => {
        const selectedCourseId = selectedCourse?.id;

        if (!selectedCourseId || transferModePending || placeReservationBlocked) {
            return;
        }

        const runTransfer = async () => {
            // Phase 14 / D-07: Refunds auch im Placereservation-Umbuchen-Flow vor Transfer ausführen
            if (transformAction === "transfer" && isSettled) {
                await executePendingRefunds();
            }
            if (transformAction === "copy") {
                await copyBooking({
                    variables: {
                        bookingId,
                        targetCourseId: selectedCourseId,
                        usePlaceReservation,
                    },
                }).then((res) => {
                    handleSuccess(res, selectedCourse, transformAction);
                });
            } else {
                await transferAttendee({
                    variables: {
                        bookingId,
                        targetCourseId: selectedCourseId,
                        usePlaceReservation,
                    },
                }).then((res) => {
                    handleSuccess(res, selectedCourse, transformAction);
                });
            }
        };

        try {
            await runTransfer();
        } catch (error) {
            // Transfer-FK / Link-Void: aktiver Zahlungslink → Void→Retry, sonst generisch.
            if (handleTransferError("transferOrCopyAttendeeToAnotherCoursePlacereservation", error, runTransfer)) {
                return;
            }
            message.error(`Refund/Umbuchung fehlgeschlagen: ${error instanceof Error ? error.message : String(error)}`, 5);
            handleError("transferOrCopyAttendeeToAnotherCoursePlacereservation", error, "modal", knownErrors);
        }
    };

    // TEXT -----------------------------------------------------------------------------------------------------

    const modalTitle =
        transformAction === "copy" ? (
            <span>
                <span className={css.modalTitlePrimary}>{`Weitere Buchung anlegen für `}</span>
                <span className={css.modalTitleSecondary}>{`${attendeeFullName}`}</span>
            </span>
        ) : isSettled ? (
            <span>
                <span className={css.modalTitlePrimary}>{`Buchung stornieren und Teilnehmer in anderen Kurs verschieben `}</span>
                <span className={css.modalTitleSecondary}>{`${attendeeFullName}`}</span>
            </span>
        ) : (
            <span>
                <span className={css.modalTitlePrimary}>{`Teilnehmer in anderen Kurs verschieben `}</span>
                <span className={css.modalTitleSecondary}>{`${attendeeFullName}`}</span>
            </span>
        );

    const confirmButtonText = (freePlaces: number) => {
        if (transformAction === "copy") {
            if (freePlaces <= 0) {
                return "Kopieren und auf die Warteliste des Kurses setzen";
            }

            return "Kopieren und in diesen Kurs buchen";
        } else if (freePlaces <= 0) {
            return `Teilnehmer auf die Warteliste des Kuses verschieben`;
        }

        if (isSettled) {
            return `Buchung stornieren und Teilnehmer in diesen Kurs verschieben`;
        }

        return `Teilnehmer in diesen Kurs verschieben`;
    };

    const placeReservationTexts = (): {
        buttonText: string;
        modalTitle: React.ReactNode | string;
        modalContent: React.ReactNode | string;
        action: () => void;
    } => {
        if (transformAction === "copy") {
            return {
                buttonText: "Kopieren und Platzreservierung belegen",
                modalTitle: "Weitere Buchung anlegen (Weitermeldung)",
                modalContent: (
                    <span style={{whiteSpace: "pre-line"}}>
                        <div style={{fontWeight: 500}}>{"Bei dieser Aktion wird der Teilnehmer in einen anderen Kurs kopiert"}</div>
                        <div>{`- Der ausgewählte Kurs hat mindestens eine Platzreservierung, diese wird verwendet.\n- Die aktuelle Buchung bleibt unverändert.`}</div>
                    </span>
                ),
                action: async () => transferOrCopyAttendeeToAnotherCoursePlacereservation(true),
            };
        } else if (isSettled) {
            return {
                buttonText: "Buchung stornieren und Platzreservierung belegen",
                modalTitle: "Buchung stornieren und Teilnehmer verschieben",
                modalContent: (
                    <span style={{whiteSpace: "pre-line"}}>
                        <div style={{fontWeight: 500}}>{"Bei dieser Aktion wird der Teilnehmer in einen anderen Kurs verschoben"}</div>
                        <div>{`- Der ausgewählte Kurs hat mindestens eine Platzreservierung, diese wird verwendet.\n- Die aktuelle Buchung wird storniert.`}</div>
                    </span>
                ),
                action: async () => transferOrCopyAttendeeToAnotherCoursePlacereservation(true),
            };
        }

        return {
            buttonText: "Verschieben und Platzreservierung belegen",
            modalTitle: "Teilnehmer verschieben",
            modalContent: (
                <span style={{whiteSpace: "pre-line"}}>
                    <div style={{fontWeight: 500}}>{"Bei dieser Aktion wird der Teilnehmer in einen anderen Kurs verschoben"}</div>
                    <div>{`- Der ausgewählte Kurs hat mindestens eine Platzreservierung, diese wird verwendet.`}</div>
                </span>
            ),
            action: async () => transferOrCopyAttendeeToAnotherCoursePlacereservation(true),
        };
    };

    // Der Platzreservierungs-Zweig bleibt Legacy (`transferAttendeeToCourse`). Im Wizard-Modus
    // haengt derselbe Knopf im Schritt „Zielkurs" des Wizards; Mitarbeitenden-Zugaengen wird
    // er dort nicht angeboten, weil er die E1-Regeln der Engine umgehen wuerde (#799).
    const placeReservationButton = (disabled: boolean) => placervationsEntries.length > 0 && !placeReservationBlocked && (
        <Button
            onClick={() => {
                Modal.confirm({
                    width: 600,
                    icon: null,
                    bodyStyle: {padding: marginS},
                    title: placeReservationTexts().modalTitle,
                    content: placeReservationTexts().modalContent,
                    onCancel: () => {},
                    okText: placeReservationTexts().buttonText,
                    onOk: placeReservationTexts().action,
                    closable: true,
                });
            }}
            disabled={!selectedCourse || disabled || isOperationInProgress || transferModePending}
        >
            {placeReservationTexts().buttonText}
        </Button>
    );

    const ModalFooter = ({freePlaces}: {freePlaces: number}) => {
        return (
            <div>
                <Checkbox checked={deleteNotes} onChange={(e) => setDeleteNotes(e.target.checked)} style={{marginLeft: 10, marginRight: 10}}>
                    {`Notizen löschen`}
                    <Tooltip title="Wenn aktiviert, werden die Notizen sowohl des Buchers als auch der Mitarbeiter in der neuen Buchung entfernt.">
                        <InfoCircleTwoTone style={{marginLeft: 5}} />
                    </Tooltip>
                </Checkbox>
                <Button danger onClick={() => setModalOpen(false)}>
                    {"Abbrechen"}
                </Button>
                {placeReservationButton(false)}
                <Button
                    type="primary"
                    loading={isOperationInProgress}
                    onClick={
                        isSettled
                            ? transformAction === "copy"
                                ? () => {
                                      Modal.confirm({
                                          icon: null,
                                          title: "Teilnehmer in anderen Kurs kopieren",
                                          content: "Bei dieser Aktion wird der Teilnehmer in einen anderen Kurs kopiert. Die aktuelle Buchung bleibt unverändert.",
                                          onCancel: () => {},
                                          okText: "Teilnehmer kopieren",
                                          onOk: transferOrCopyAttendeeToAnotherCourse,
                                      });
                                  }
                                : () => {
                                      Modal.confirm({
                                          icon: null,
                                          title: "Stornieren und umbuchen",
                                          content: "Bei dieser Aktion wird die aktuelle Buchung storniert und eine Stornorechnung erstellt. Der Teilnehmer wird in den ausgewählten Kurs übertragen.",
                                          onCancel: () => {},
                                          okText: "Stornieren und umbuchen",
                                          onOk: transferOrCopyAttendeeToAnotherCourse,
                                      });
                                  }
                            : transferOrCopyAttendeeToAnotherCourse
                    }
                    disabled={!selectedCourse || isOperationInProgress || transferModePending}
                >
                    {confirmButtonText(freePlaces)}
                </Button>
            </div>
        );
    };

    // MODAL -----------------------------------------------------------------------------------------------------

    if (engineTransferEnabled) {
        return (
            <TransferWizard
                bookingId={bookingId}
                currentCourseId={currentCourseId}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                attendeeFullName={attendeeFullName}
                onCourseSelected={setSelectedCourse}
                placeReservationButton={(_course, disabled) => placeReservationButton(disabled)}
            />
        );
    }

    return (
        <Modal
            title={modalTitle}
            open={modalOpen}
            onCancel={() => setModalOpen(false)}
            width="calc(100% - 120px)"
            style={{
                top: 30,
                padding: 0,
            }}
            footer={<ModalFooter freePlaces={selectedCourse?.freePlaces ?? 0} />}
            destroyOnClose
        >
            {transferModeUnavailable && transformAction === "transfer" && <Alert type="error" showIcon message={i18n.containers.bookings.TransferEngineConfirm.configurationFailed()} />}
            {transformAction === "transfer" && isSettled && <ActiveDunningAlert bookingId={bookingId} variant="transfer" />}
            <CourseList
                tableData={tableData}
                options={options}
                setOptions={setOptions}
                refetch={refetchCourses}
                defaultPageSize={10}
                bookingCourse
                toolBarButtons={["columnSelector"]}
                hiddenColumns={["actions"]}
                defaultColumns={["prefixedCourseNumber", "courseType", "firstCourseLesson", "location", "freePlaces", "placeReservationsCount"]}
                handleCourseSelection={(selectedCourses: Array<TableType>) => {
                    setSelectedCourse(selectedCourses[0]);
                }}
                tablesEnum={TablesEnum.CourseTransferAttendeeToCourse}
            />
            {/* Phase 14 / D-07 + D-10: Inline Refund-Panel für Umbuchen-Flow.
                Sichtbar nur wenn: Transfer-Aktion + abgerechnete Buchung + Zielkurs gewählt + Mollie-SPs vorhanden */}
            {transformAction === "transfer" && isSettled && selectedCourse && !transferModePending && mollieRefundableSps.length > 0 && (
                <>
                    <Divider />
                    <RefundPanel
                        mode="umbuchen"
                        scheduledPayments={mollieRefundableSps}
                        onSelectionChange={handleRefundSelectionChange}
                    />
                </>
            )}
        </Modal>
    );
};

export default memo(TransferAttendeeToAnotherCourse);
